import type { Coordinates } from "@/types";

/**
 * Calcola la distanza in km tra due coordinate usando la formula di Haversine
 */
export function haversineDistance(coord1: Coordinates, coord2: Coordinates): number {
  const R = 6371; // Raggio della Terra in km
  const toRad = (deg: number) => (deg * Math.PI) / 180;

  const dLat = toRad(coord2.lat - coord1.lat);
  const dLon = toRad(coord2.lon - coord1.lon);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(coord1.lat)) *
      Math.cos(toRad(coord2.lat)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

/**
 * Ordina un array di items con coordinate per distanza
 */
export function sortByDistance<T extends { coordinates?: Coordinates }>(
  items: T[],
  userLocation: Coordinates
): (T & { distance: number })[] {
  return items
    .filter((item) => item.coordinates)
    .map((item) => ({
      ...item,
      distance: haversineDistance(userLocation, item.coordinates!),
    }))
    .sort((a, b) => a.distance - b.distance);
}

/**
 * Formatta nome regione da slug (es. "campania" -> "Campania", "emilia-romagna" -> "Emilia-Romagna")
 */
export function formatRegioneDisplayName(slug: string): string {
  return slug
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("-");
}

/**
 * Formatta nome provincia da sigla (es. "sa" -> "SA", "rm" -> "RM")
 */
export function formatProvinciaDisplayName(sigla: string): string {
  return sigla.toUpperCase();
}

/**
 * Formatta nome comune da slug (es. "san-cipriano-picentino" -> "San Cipriano Picentino")
 */
export function formatComuneDisplayName(slug: string): string {
  return slug
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
